import { formatPropertyAddress } from '../utils/propertyAddress';
import { propertyFetch } from '../services/idxPropertyClient';
import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { SITE_CONFIG } from '../config';
import { SEO_CONFIG } from '../config/seo';
import { useTheme } from '../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import './IDXTownPage.css';
import SEOLinks from './SEOLinks';

const MidwayCityHomesForSalePage = () => {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();
  const [properties, setProperties] = useState([]);
  const [propertyImages, setPropertyImages] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.body.classList.remove('intro-active');
    document.body.style.overflow = 'auto';
    document.body.style.position = 'static';
    if (isDarkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
    };
  }, [isDarkMode]);

  useEffect(() => {
    const fetchProperties = async () => {
      try {
        let response = await propertyFetch(`https://tophamtechbe.com/api/treas/idx/v1/retrieve-property-summary?city=Midway+City&county=Orange&state=CA&firstPhoto=true&hasMedia=true&limit=9`, {});
        let data = response.ok ? await response.json() : { properties: [] };
        let props = data.properties || [];

        // Fall back to Westminster if no Midway City results
        if (props.length === 0) {
          response = await propertyFetch(`https://tophamtechbe.com/api/treas/idx/v1/retrieve-property-summary?city=Westminster&county=Orange&state=CA&firstPhoto=true&hasMedia=true&limit=9`, {});
          if (response.ok) {
            data = await response.json();
            props = data.properties || [];
          }
        }

        setProperties(props);
        props.forEach(p => {
          if (p.listing_key) {
            propertyFetch(`https://tophamtechbe.com/api/treas/idx/v1/retrieve-property-media/${p.listing_key}`, {}).then(r => r.json()).then(d => {
              const first = (d.media || []).sort((a, b) => parseInt(a.order_hint) - parseInt(b.order_hint))[0];
              const url = first?.media_url || first?.s3_link;
              if (url) setPropertyImages(prev => ({ ...prev, [p.listing_key]: url }));
            }).catch(() => {});
          }
        });
      } catch (error) {
        console.error('Error fetching Midway City properties:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProperties();
  }, []);

  return (
    <div className="idxtown-page">
      <Helmet>
        <title>Midway City Homes for Sale | Affordable Near Westminster | Truc Doan</title>
        <meta name="description" content="Find affordable homes in Midway City CA near Westminster and Little Saigon. Small-town charm with big-city amenities nearby. Truc Doan, your local realtor." />
        <meta name="keywords" content="Midway City homes for sale, Midway City CA real estate, affordable homes near Westminster, Midway City houses, buy home Midway City, near Little Saigon affordable, Truc Doan Midway City, Orange County affordable homes" />
        <link rel="canonical" href={`${SEO_CONFIG.DOMAIN}/midway-city-homes-for-sale`} />
        <meta property="og:title" content="Midway City Homes for Sale | Affordable Near Westminster | Truc Doan" />
        <meta property="og:description" content="Discover affordable homes in Midway City near Westminster and Little Saigon. Small-town charm with excellent location. Expert guidance from Truc Doan." />
        <meta property="og:url" content={`${SEO_CONFIG.DOMAIN}/midway-city-homes-for-sale`} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={SEO_CONFIG.OG_IMAGE} />
        <meta name="geo.region" content="US-CA" />
        <meta name="geo.placename" content="Midway City, California" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "RealEstateAgent",
            "name": "Truc Doan",
            "telephone": "+1-818-818-0989",
            "email": "doanestate@gmail.com",
            "url": `${SEO_CONFIG.DOMAIN}/midway-city-homes-for-sale`,
            "image": SEO_CONFIG.OG_IMAGE,
            "description": "Truc Doan helps buyers find affordable homes in Midway City CA, a small unincorporated community near Westminster and Little Saigon with excellent value.",
            "areaServed": {
              "@type": "Place",
              "name": "Midway City, California"
            },
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "Westminster",
              "addressRegion": "CA",
              "postalCode": "92683",
              "addressCountry": "US"
            }
          })}
        </script>
      </Helmet>

      <div className="idxtown-container">
        <button
          onClick={() => navigate('/')}
          style={{ position: 'absolute', top: '20px', left: '20px', padding: '10px 20px', background: 'rgb(14, 165, 233)', color: 'rgb(243, 244, 246)', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '16px', fontWeight: 'bold', zIndex: 10 }}
        >
          {t('nav.home')}
        </button>

        <header className="idxtown-header">
          <h1>Midway City Homes for Sale</h1>
          <p style={{ marginTop: '16px', fontSize: '18px', opacity: 0.9 }}>
            Discover affordable homes in Midway City — a hidden gem nestled between Westminster and Huntington Beach with small-town charm.
          </p>
        </header>

        <section className="idxtown-vibe">
          <h2>Why Buy a Home in Midway City?</h2>
          <p>Midway City is one of Orange County's best-kept secrets for affordable homeownership. This small unincorporated community sits between Westminster and Huntington Beach, offering buyers significantly lower price points while maintaining immediate access to Little Saigon's restaurants, shops, and cultural amenities. For first-time buyers and investors seeking value in Orange County, Midway City delivers exceptional opportunity.</p>
          <p>Despite its compact size, Midway City benefits from its strategic location. Residents enjoy Westminster's Vietnamese community amenities, Huntington Beach's coastal lifestyle, and Garden Grove's family resources — all within minutes. The area's affordability, combined with proximity to major employment centers and cultural districts, makes it an increasingly popular choice for savvy homebuyers who want Orange County living without the premium price tag.</p>
        </section>

        <section className="idxtown-lifestyle">
          <h2>Midway City Neighborhood & Amenities</h2>
          <p>Midway City offers a small-town community atmosphere with convenient access to the amenities of surrounding cities.</p>
          <div className="amenities-list">
            <div className="amenity">
              <h3>Affordable Entry Point</h3>
              <p>One of Orange County's most affordable communities for homeownership. Median prices well below surrounding cities make this ideal for first-time buyers.</p>
            </div>
            <div className="amenity">
              <h3>Steps from Little Saigon</h3>
              <p>Walk or drive minutes to Bolsa Avenue's Vietnamese restaurants, Asian Garden Mall, specialty markets, and cultural events in neighboring Westminster.</p>
            </div>
            <div className="amenity">
              <h3>Small-Town Community</h3>
              <p>A close-knit neighborhood feel that larger cities can't replicate. Friendly neighbors, quiet streets, and a genuine sense of community belonging.</p>
            </div>
            <div className="amenity">
              <h3>Near Huntington Beach</h3>
              <p>Just minutes from the beach, Midway City residents enjoy coastal access without coastal prices. Surf, sand, and boardwalk are a short drive away.</p>
            </div>
            <div className="amenity">
              <h3>Westminster Schools</h3>
              <p>Served by Westminster School District and Huntington Beach Union High School District, providing quality education options for families.</p>
            </div>
            <div className="amenity">
              <h3>Investment Potential</h3>
              <p>As surrounding areas appreciate, Midway City properties offer strong investment upside. Growing demand from buyers priced out of Westminster and HB.</p>
            </div>
          </div>
        </section>

        <section className="market-snapshot">
          <h2>Midway City Real Estate Market Snapshot</h2>
          <p>Midway City offers some of the most competitive pricing in Orange County, attracting first-time buyers and investors seeking value near premium communities.</p>
          <table className="market-stats">
            <thead>
              <tr>
                <th>Market Stat</th>
                <th>Value</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Median Home Price (Midway City)</td>
                <td>~$650,000</td>
              </tr>
              <tr>
                <td>Average Days on Market</td>
                <td>15–25 Days</td>
              </tr>
              <tr>
                <td>Most Popular Price Range</td>
                <td>$450K – $750K</td>
              </tr>
              <tr>
                <td>Property Types</td>
                <td>Single Family, Condos, Duplexes</td>
              </tr>
              <tr>
                <td>Top Draws</td>
                <td>Affordability, Location, Upside</td>
              </tr>
            </tbody>
          </table>
        </section>

        <section className="properties-section">
          <h2>Current Homes for Sale in Midway City & Nearby</h2>
          {loading ? (
            <p style={{ color: 'rgb(156, 163, 175)' }}>{t('property.loadingProperties')}</p>
          ) : properties.length > 0 ? (
            <div className="properties-grid">
              {properties.slice(0, 9).map((property) => (
                <div
                  key={property.listing_key}
                  className="property-card"
                  onClick={() => navigate(`/listing/${property.listing_key}`)}
                  style={{
                    backgroundImage: (propertyImages[property.listing_key] || property.first_photo_url) ? `url(${propertyImages[property.listing_key] || property.first_photo_url})` : 'none',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                  }}
                >
                  <div className="property-price">${property.current_price?.toLocaleString()}</div>
                  <div className="property-address">
                    {formatPropertyAddress(property)}, CA {property.postal_code}
                  </div>
                  <div className="property-type">{property.property_sub_type}</div>
                  {(property.list_agent_full_name || property.list_office_name) && (
                    <div style={{fontSize: '11px', color: '#ddd', marginTop: '2px'}}>{t('landing.listedBy')}: {[property.list_agent_full_name, property.list_office_name].filter(Boolean).join(' | ')}</div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p style={{ color: 'rgb(156, 163, 175)' }}>
              Browse all available Midway City and Westminster listings by clicking "Search Listings" below.
            </p>
          )}
        </section>

        <section className="cta-section">
          <h2>Ready to Buy or Sell in Midway City?</h2>
          <p>Contact Truc Doan today for a free consultation. I'll help you find affordable homes in Midway City and the Westminster area that fit your budget and lifestyle.</p>
          <div className="cta-buttons">
            <a href="mailto:doanestate@gmail.com?subject=Midway City Real Estate Inquiry" className="cta-button primary">
              Email Truc Doan
            </a>
            <a href="tel:+1-818-818-0989" className="cta-button secondary">
              Call +1-818-818-0989
            </a>
            <button className="cta-button primary" onClick={() => navigate('/chat')} style={{ border: 'none', cursor: 'pointer' }}>
              Search Midway City Listings
            </button>
          </div>
        </section>
      </div>

      <div style={{ background: 'rgba(0,0,0,0.85)', padding: '6px 12px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        <img src="https://aw-client-logos-and-headshots.s3.us-east-2.amazonaws.com/logos/crmlslogo.png" alt="CRMLS" style={{ height: '20px', objectFit: 'contain' }} />
        <img src="https://aw-client-logos-and-headshots.s3.us-east-2.amazonaws.com/logos/equalhousinglogo.png" alt="Equal Housing Opportunity" style={{ height: '20px', objectFit: 'contain' }} />
        <span style={{ fontSize: '10px', color: '#ccc', flex: 1 }}>
          Based on information from California Regional Multiple Listing Service, Inc. and/or other sources. All data, including all measurements and calculations of area, is obtained from various sources and has not been, and will not be, verified by broker or MLS. All information should be independently reviewed and verified for accuracy. Properties may or may not be listed by the office/agent presenting the information.
        </span>
      </div>
      <SEOLinks />
    </div>
  );
};

export default MidwayCityHomesForSalePage;
